import type { ReactNode } from 'react'
import s from './ColumnSort.module.css'

/** L'ordre d'une colonne : croissant, décroissant, ou pas triée par elle. */
export type SortDirection = 'asc' | 'desc'

export interface ColumnSortProps {
  /** Le sens du tri **sur cette colonne**, ou `null` si le tri porte sur une autre. */
  direction: SortDirection | null
  /** Appelé au clic et au clavier. C'est l'écran qui décide du sens suivant. */
  onSort?: () => void
  children: ReactNode
}

/**
 * Le libellé **actionnable** d'un en-tête de colonne triable — report de `.th-sort` du
 * prototype back-office, dont tous les en-têtes du tableau des demandes portent un.
 *
 * Il se pose **dans** le `<th>`, qui reste à l'écran : c'est lui qui connaît la largeur de sa
 * colonne, son alignement, et l'état `aria-sort` que les lecteurs d'écran annoncent.
 *
 * ```tsx
 * <th aria-sort={sort === 'reference' ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
 *   <ColumnSort direction={sort === 'reference' ? direction : null} onSort={() => sortBy('reference')}>
 *     Réf. GHP
 *   </ColumnSort>
 * </th>
 * ```
 *
 * ⚠️ **Un `<button>` réel, et pas un `onClick` sur le `<th>`.** Le prototype pose son gestionnaire
 * sur la cellule ; un en-tête de tableau n'est ni focalisable ni activable au clavier, et le tri
 * d'une liste de six mille lignes ne doit pas être réservé à la souris.
 *
 * ⚠️ **`aria-sort` appartient au `<th>`, pas à ce bouton** — c'est la cellule d'en-tête que la
 * spécification ARIA désigne, et un lecteur d'écran l'annonce en parcourant le tableau, pas en
 * atteignant le bouton. Le poser ici le rendrait muet là où il compte.
 *
 * ⚠️ **Le chevron est décoratif** (`aria-hidden`) : il redit ce qu'`aria-sort` porte déjà, et
 * l'annoncer ferait lire « flèche vers le haut croissant » sur chaque en-tête.
 */
export function ColumnSort({ direction, onSort, children }: ColumnSortProps) {
  return (
    <button
      type="button"
      className={s.root}
      // L'état va au DOM plutôt qu'à une classe : c'est la règle du paquet, et le CSS le lit.
      data-sorted={direction === null ? undefined : ''}
      onClick={onSort}
      disabled={onSort === undefined}
    >
      {children}
      {direction !== null && (
        <span className={s.caret} aria-hidden>
          {direction === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </button>
  )
}
